import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, dateFr, apiError } from "@/lib/api";
import Modal from "@/components/Modal";

export default function Clients() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", adresse: "", type_client: "particulier", siret: "", raison_sociale: "", tva_intracom: "" });
  const [busy, setBusy] = useState(false);
  const [archives, setArchives] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(archives ? "/clients?archives=true" : "/clients").then((r) => setClients(r.data));
  }, [archives]);

  const filtered = clients.filter((c) =>
    c.nom.toLowerCase().includes(q.toLowerCase()) || (c.telephone || "").includes(q));

  async function createClient() {
    if (!form.nom.trim()) return toast.error(t("clients.nom_requis"));
    setBusy(true);
    try {
      const { data } = await api.post("/clients", form);
      toast.success(t("clients.cree", { nom: data.nom }));
      setAddOpen(false);
      navigate(`/clients/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="page-clients">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("clients.titre")}</h1>
        <button onClick={() => setAddOpen(true)} className="btn-accent" data-testid="btn-nouveau-client">
          <Plus size={20} /> {t("clients.nouveau")}
        </button>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
        <input className="input-field !pl-11" placeholder={t("clients.recherche")} value={q}
               onChange={(e) => setQ(e.target.value)} data-testid="input-recherche-client" />
      </div>

      <div className="flex justify-end">
        <button onClick={() => setArchives(!archives)} className="text-[#8A5A2B] text-sm font-semibold"
                data-testid="toggle-clients-archives">
          {archives ? t("clients.retour_actifs") : t("clients.voir_archives")}
        </button>
      </div>

      <div className="panel !p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-stone-500 text-sm py-10 text-center">
            {archives ? t("clients.vide_archives") : t("clients.vide")}
          </p>
        ) : (
          <div className="divide-y divide-[#E6DFD1]" data-testid="liste-clients">
            {filtered.map((c) => (
              <button key={c.id} onClick={() => navigate(`/clients/${c.id}`)}
                      className="w-full flex items-center gap-4 p-4 hover:bg-[#FDFBF7] text-left"
                      data-testid={`row-client-${c.id}`}>
                <div className="w-12 h-12 rounded-full bg-[#12100D] text-[#D9A461] flex items-center justify-center font-serif font-bold text-lg shrink-0">
                  {c.nom.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{c.nom}</div>
                  <div className="text-xs text-stone-500 truncate flex items-center gap-1">
                    {c.telephone && <><Phone size={11} /> {c.telephone} · </>}{t("clients.depuis", { date: dateFr(c.cree_le) })}
                  </div>
                </div>
                {c.statut === "archive" && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFEAE0] text-[#8A5A2B] shrink-0"
                        data-testid={`badge-archive-${c.id}`}>{t("client_detail.archive")}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t("clients.modal_titre")} testid="modal-nouveau-client">
        <div className="space-y-4">
          <input className="input-field" placeholder={t("clients.nom")} value={form.nom}
                 onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="input-client-nom" />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setForm({ ...form, type_client: "particulier" })}
                    className={`h-12 rounded-lg border font-semibold text-sm transition-colors ${form.type_client === "particulier" ? "bg-[#12100D] text-[#D9A461] border-[#12100D]" : "bg-white text-stone-500 border-[#E6DFD1]"}`}
                    data-testid="select-type-particulier">
              {t("clients.particulier")}
            </button>
            <button type="button" onClick={() => setForm({ ...form, type_client: "professionnel" })}
                    className={`h-12 rounded-lg border font-semibold text-sm transition-colors ${form.type_client === "professionnel" ? "bg-[#12100D] text-[#D9A461] border-[#12100D]" : "bg-white text-stone-500 border-[#E6DFD1]"}`}
                    data-testid="select-type-professionnel">
              {t("clients.professionnel")}
            </button>
          </div>
          {form.type_client === "professionnel" && (
            <div className="space-y-4 border border-[#E6DFD1] rounded-lg p-3 bg-[#FDFBF7]" data-testid="bloc-champs-pro">
              <input className="input-field" placeholder={t("clients.siret")} value={form.siret}
                     onChange={(e) => setForm({ ...form, siret: e.target.value })} data-testid="input-client-siret" />
              <input className="input-field" placeholder={t("clients.raison_sociale")} value={form.raison_sociale}
                     onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })} data-testid="input-client-raison-sociale" />
              <input className="input-field" placeholder={t("clients.tva_intra")} value={form.tva_intracom}
                     onChange={(e) => setForm({ ...form, tva_intracom: e.target.value })} data-testid="input-client-tva" />
              <p className="text-xs text-stone-500">{t("clients.pro_note")}</p>
            </div>
          )}
          <input className="input-field" placeholder={t("clients.telephone")} value={form.telephone}
                 onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="input-client-telephone" />
          <input className="input-field" type="email" placeholder={t("clients.email_optionnel")} value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-client-email" />
          <input className="input-field" placeholder={t("clients.adresse_optionnel")} value={form.adresse}
                 onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="input-client-adresse" />
          <button onClick={createClient} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-client">
            {busy ? <Loader2 className="animate-spin" size={18} /> : t("clients.creer")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
